import { flow } from 'fp-ts/function';
import { get } from 'lodash/fp';
import { createSelector } from 'reselect';

import { getStringProp } from '@mews/fp';
import { composeProps } from '@mews/framework/redux';

import { getCurrentDateEnterpriseAvailability } from '../../selectors/availability';
import { parentSelector } from '../../selectors/parentSelector';
import { ApplicationState } from '../../types';
import {
    getDefaultAdultCount,
    getDefaultChildCount,
    getEnterpriseChildSelectionEnabled,
    getMaxAdultCount,
} from '../configuration/selectors';
import { getUniqueAgeCategoriesPerClassification } from '../entities/ageCategories/selectors';
import { serviceEnterpriseIdPropsExtender } from '../entities/services/selectors';
import { categoryEnterpriseIdPropsExtender } from '../resourceCategories/selectors';
import { Occupancy } from '../resourceOrder/occupancyTypes';
import { OCCUPANCY_SLICE_NAME } from './reducer';
import { areBothOccupanciesZero, isValidOccupancyCount } from './utils';

const moduleSelector = createSelector<ApplicationState, ApplicationState, Occupancy>(
    parentSelector,
    get(['views', OCCUPANCY_SLICE_NAME])
);

const getInitialAdultCount = flow(moduleSelector, get('initialAdultCount'));
const getInitialChildCount = flow(moduleSelector, get('initialChildCount'));

const getBaseOccupancies = createSelector(
    getDefaultAdultCount,
    getDefaultChildCount,
    getInitialAdultCount,
    getInitialChildCount,
    getEnterpriseChildSelectionEnabled,
    getMaxAdultCount,
    (
        defaultConfigurationAdultCount,
        defaultConfigurationChildCount,
        initialAdultCount,
        initialChildCount,
        enableChildSelection,
        maxAdultCount
    ) => {
        if (areBothOccupanciesZero(initialAdultCount, initialChildCount)) {
            return { adultCount: defaultConfigurationAdultCount, childCount: defaultConfigurationChildCount };
        }

        let childCount = null;

        if (enableChildSelection === true) {
            childCount = isValidOccupancyCount(initialChildCount) ? initialChildCount : defaultConfigurationChildCount;
        }

        let adultCount = isValidOccupancyCount(initialAdultCount) ? initialAdultCount : defaultConfigurationAdultCount;

        if (
            isValidOccupancyCount(adultCount) &&
            isValidOccupancyCount(maxAdultCount) &&
            maxAdultCount !== 0 &&
            adultCount > maxAdultCount
        ) {
            adultCount = maxAdultCount;
        }

        return { adultCount, childCount };
    }
);

const getServiceEnterpriseBaseOccupancies = composeProps(
    (state, { enterpriseId }) =>
        enterpriseId != null ? getBaseOccupancies(state, { enterpriseId }) : { adultCount: null, childCount: null },
    serviceEnterpriseIdPropsExtender
);

const getServiceEnterpriseChildSelectionEnabled = composeProps(
    (state, { enterpriseId }) =>
        enterpriseId != null ? getEnterpriseChildSelectionEnabled(state, { enterpriseId }) : null,
    serviceEnterpriseIdPropsExtender
);

export const getInitialOccupancyData = createSelector(
    getServiceEnterpriseBaseOccupancies,
    getUniqueAgeCategoriesPerClassification,
    getServiceEnterpriseChildSelectionEnabled,
    ({ adultCount, childCount }, { adultAgeCategoryId, childAgeCategoryId }, enableChildSelection) => {
        const occupancyData = [];

        if (adultAgeCategoryId != null) {
            occupancyData.push({ ageCategoryId: adultAgeCategoryId, personCount: adultCount ?? 0 });
        }

        if (childAgeCategoryId != null && enableChildSelection === true) {
            occupancyData.push({ ageCategoryId: childAgeCategoryId, personCount: childCount ?? 0 });
        }

        return occupancyData;
    }
);
export const getCategoryEnterpriseBaseOccupancies = composeProps(
    (state, { enterpriseId }) => (enterpriseId != null ? getBaseOccupancies(state, { enterpriseId }) : null),
    categoryEnterpriseIdPropsExtender
);

const getCurrentDateCategoryEnterpriseAvailability = composeProps(
    (state, { enterpriseId }) =>
        enterpriseId != null ? getCurrentDateEnterpriseAvailability(state, { enterpriseId }) : null,
    categoryEnterpriseIdPropsExtender
);

export const getCurrentDateCategoryEnterpriseMaximumAvailabilityCount = createSelector(
    getCurrentDateCategoryEnterpriseAvailability,
    getStringProp('categoryId'),
    (currentDateAvailability, categoryId) => currentDateAvailability?.categories?.[categoryId]?.availableCount ?? 0
);
